(() => {
  'use strict';
  if (window.AWPredictivePlatformBridge?.version) return;

  const VERSION = '1.0.0';
  const STATE_SELECTOR = '[data-aw-predictive-compatibility-state]';
  const SETTINGS_SELECTOR = '[data-aw-predictive-search-settings]';

  const clean = (value = '') => String(value ?? '').replace(/\s+/g, ' ').trim();
  const normalize = (value = '') =>
    clean(value)
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLowerCase();

  const pathOf = (href = '') => {
    try { return new URL(href, location.href).pathname.replace(/\/+$/, '') || '/'; }
    catch (_) { return ''; }
  };

  const safeLanding = (path) =>
    /^\/pages\/compatibilite-airsoft-[a-z0-9-]+$/i.test(String(path || ''));

  const parseState = () => {
    const node = document.querySelector(STATE_SELECTOR);
    if (!node) return null;
    try { return JSON.parse(node.textContent || '{}'); }
    catch (_) { return null; }
  };

  const candidates = (state) => {
    const visibility = Array.isArray(state?.search_visibility_queue)
      ? state.search_visibility_queue
          .filter((item) =>
            item &&
            item.platform &&
            safeLanding(item.landing_path) &&
            (
              item.class === 'visibilite_recherche' ||
              String(item.action || '').includes('routing')
            )
          )
          .map((item) => ({ ...item, queue: 'visibility' }))
      : [];

    const disambiguation = Array.isArray(state?.disambiguation_queue)
      ? state.disambiguation_queue
          .filter((item) => item && item.query && safeLanding(item.landing_path))
          .map((item) => ({
            ...item,
            platform: item.platform || item.query,
            queue: 'disambiguation'
          }))
      : [];

    return [...visibility, ...disambiguation];
  };

  const findCandidate = (state, query) =>
    candidates(state).find((item) => normalize(item.query) === normalize(query)) || null;

  const escapeHtml = (value) =>
    String(value == null ? '' : value).replace(/[&<>'"]/g, (char) => ({
      '&': '&amp;',
      '<': '&lt;',
      '>': '&gt;',
      "'": '&#39;',
      '"': '&quot;'
    }[char]));

  const trackingEnabled = () =>
    document.querySelector(SETTINGS_SELECTOR)?.dataset.awPlatformBridgeTracking === 'true';

  const track = (name, payload) => {
    if (!trackingEnabled()) return;
    try {
      window.AWAnalytics?.track?.(name, {
        ranking_mode: 'analytics_bridge',
        ranking_surface: 'predictive_search',
        ...payload
      });
    } catch (_) {}
  };

  const ensureGroup = (grid) => {
    let group = grid.querySelector('.predictive-search-premium__group--compatibility');
    if (group) return group;

    const holder = document.createElement('div');
    holder.innerHTML =
      '<section class="predictive-search-premium__group predictive-search-premium__group--compatibility">' +
        '<div class="predictive-search-premium__heading"><h3>Compatibility AW</h3><span>0</span></div>' +
        '<ul></ul>' +
      '</section>';

    group = holder.firstElementChild;
    const productGroup = grid.querySelector('.predictive-search-premium__group--products');
    if (productGroup) productGroup.insertAdjacentElement('afterend', group);
    else grid.prepend(group);
    return group;
  };

  const syncCounts = (panel) => {
    panel.querySelectorAll('.predictive-search-premium__group').forEach((group) => {
      const badge = group.querySelector('.predictive-search-premium__heading span');
      const list = group.querySelector(':scope > ul');
      if (badge && list) badge.textContent = String(list.children.length);
    });
    const total = panel.querySelectorAll('.predictive-search-premium__group > ul > li').length;
    const summary = panel.querySelector('.predictive-search-premium__summary > span');
    if (summary) summary.textContent = `${total} suggestion${total > 1 ? 's' : ''}`;
  };

  const removePrevious = (panel) => {
    panel.querySelectorAll('[data-aw-predictive-platform-bridge]').forEach((node) => node.remove());
    panel.querySelectorAll('.predictive-search-premium__group--compatibility').forEach((group) => {
      const list = group.querySelector(':scope > ul');
      if (list && !list.children.length) group.remove();
    });
  };

  const apply = (overlay, panel) => {
    if (!overlay || !panel || panel.hidden || panel.dataset.awPlatformBridgeApplying === 'true') return;

    const input = overlay.querySelector('[data-search-input]');
    const query = clean(input?.value || '');
    const state = parseState();
    const candidate = state ? findCandidate(state, query) : null;

    panel.dataset.awPlatformBridgeApplying = 'true';
    try {
      removePrevious(panel);
      if (!candidate) {
        syncCounts(panel);
        return;
      }

      const path = String(candidate.landing_path || '');
      const platform = clean(candidate.platform || candidate.query || '');
      if (!platform || !safeLanding(path)) return;

      const grid = panel.querySelector('.predictive-search-premium__grid');
      if (!grid) return;

      const group = ensureGroup(grid);
      const list = group.querySelector('ul');
      if (!list) return;

      const existingLink = [...panel.querySelectorAll('a[data-search-option]')]
        .find((link) => pathOf(link.getAttribute('href') || '') === path);
      let item = existingLink?.closest('li') || null;

      if (item) {
        item.dataset.awPredictivePlatformBridge = 'true';
        const type = item.querySelector('.predictive-search-premium__type');
        if (type) type.textContent = 'Plateforme';
        list.prepend(item);
      } else {
        const holder = document.createElement('div');
        holder.innerHTML =
          `<li data-aw-predictive-platform-bridge>` +
            `<a role="option" aria-selected="false" data-search-option ` +
              `class="predictive-search-premium__item" href="${escapeHtml(path)}">` +
              `<span class="predictive-search-premium__type">Plateforme</span>` +
              `<strong>Compatibilité ${escapeHtml(platform)}</strong>` +
              `<span class="predictive-search-premium__arrow" aria-hidden="true">→</span>` +
            `</a>` +
          `</li>`;
        item = holder.firstElementChild;
        if (item) list.prepend(item);
      }

      if (!item) return;

      syncCounts(panel);
      panel.dataset.awPredictivePlatformBridge = 'active';

      const payload = {
        query,
        platform,
        landing_path: path,
        queue: candidate.queue,
        state_version: state.version || null
      };
      track('predictive_compatibility_bridge_exposed', payload);

      const link = item.querySelector('a[href]');
      link?.addEventListener(
        'click',
        () => track('predictive_compatibility_bridge_clicked', payload),
        { once: true }
      );
    } finally {
      panel.dataset.awPlatformBridgeApplying = 'false';
    }
  };

  const bind = () => {
    const overlay = document.querySelector('[data-search-overlay]');
    if (!overlay || overlay.dataset.awPredictivePlatformBridgeReady === 'true') return;

    const input = overlay.querySelector('[data-search-input]');
    if (!input) return;

    let timer = null;
    const schedule = () => {
      window.clearTimeout(timer);
      timer = window.setTimeout(() => {
        const panel = overlay.querySelector('#PredictiveSearchPremium');
        if (panel) apply(overlay, panel);
      }, 70);
    };

    const attach = () => {
      const panel = overlay.querySelector('#PredictiveSearchPremium');
      if (!panel || panel.dataset.awPlatformBridgeObserver === 'true') return false;
      new MutationObserver(schedule).observe(panel, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['hidden']
      });
      panel.dataset.awPlatformBridgeObserver = 'true';
      schedule();
      return true;
    };

    if (!attach()) {
      new MutationObserver((_, observer) => {
        if (!attach()) return;
        observer.disconnect();
      }).observe(overlay, { childList: true, subtree: true });
    }

    input.addEventListener('input', schedule, { passive: true });
    overlay.dataset.awPredictivePlatformBridgeReady = 'true';
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', bind, { once: true });
  } else {
    bind();
  }
  document.addEventListener('shopify:section:load', bind);

  window.AWPredictivePlatformBridge = {
    version: VERSION,
    mode: 'analytics_bridge',
    tracking: 'opt_in',
    init: bind
  };
})();
